import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react';
import { decrementCurrentStep, incrementCurrentStep } from '../../store/slices/sellerProfileSlice';
import { useSaveProfileBusinessMutation } from '../../store/services/sellerProfileCreationService';
import { useProfileCreationFormContext } from './ProfileCreationFormProvider';
import TitleText from '../atoms/TitleText';
import Button from '../atoms/Button';
import SellerPageBreadcrumbs from '../Sellers/SellerPageBreadcrumbs';

const ProfileCreationHeader = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentStep } = useSelector((state: RootState) => state.sellerProfile);
  const { triggerSubmit, isModalOpen } = useProfileCreationFormContext();
  const { sellerId } = useParams<{ sellerId: string }>();
  const profileId = sellerId || '';
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('mode') === 'edit';

  const [saveProfile, { isLoading: isSaveLoading }] = useSaveProfileBusinessMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pageTitle = isEditMode
    ? t('sellers.editSeller.pageTitle')
    : t('sellers.addSeller.pageTitle');

  const breadcrumbNamespace = isEditMode ? 'sellers.editSeller.breadcrumbs' : 'sellers.addSeller.breadcrumbs';
  const currentBreadcrumb = isEditMode
    ? t('sellers.editSeller.breadcrumbs.editProfile')
    : t('sellers.addSeller.breadcrumbs.addUser');

  const headerActionButton = isEditMode ? (
    <Button
      type="button"
      variant="primary"
      onClick={() => {
        const targetPath = profileId ? `/sellers/${profileId}/profile` : '/sellers';
        window.location.assign(targetPath);
      }}
      className="rounded-[12px] text-sm px-6 py-3 flex items-center justify-center gap-2 font-medium transition-all"
      data-testid="header-profile-button"
    >
      {t('components.viewProfile')}
    </Button>
  ) : (
    <Button
      type="button"
      variant="primary"
      disabled={isSubmitting || isSaveLoading}
      loading={isSubmitting || isSaveLoading}
      className="rounded-[12px] text-sm px-6 py-3 flex items-center justify-center gap-2 font-medium transition-all"
      onClick={async () => {
        try {
          setIsSubmitting(true);
          if ([0, 1, 2, 5].includes(currentStep)) {
            await triggerSubmit?.();
            if (currentStep !== 5) return;
          }

          if (currentStep === 5) {
            await saveProfile({ profileId }).unwrap();
            message.success(t('components.profile_saved_successfully'));
            navigate('/sellers');
            return;
          }

          dispatch(incrementCurrentStep());
        } catch (err: any) {
          console.error('Error in header submission:', err);
          message.error(err?.data?.message || t('components.somethingWentWrong'));
        } finally {
          setIsSubmitting(false);
        }
      }}
      data-testid="header-next-button"
    >
      {currentStep === 5
        ? t('components.saveInformation')
        : t('components.saveAndContinue')}
    </Button>
  );

  return (
    <div className="flex items-center justify-between mb-2 w-full" data-testid="profile-creation-header">
      <div>
        <TitleText
          size="2xl"
          className="font-bold text-gray-900 dark:text-white m-0 mb-1"
          text={pageTitle}
        />
        <SellerPageBreadcrumbs
          dashboardLabel={t(`${breadcrumbNamespace}.dashboard`)}
          listLabel={t(`${breadcrumbNamespace}.sellersList`)}
          currentLabel={currentBreadcrumb}
        />
      </div>

      <div className="flex items-center gap-3 flex-shrink-0" data-testid="profile-creation-header-actions">
        {currentStep > 0 && !isEditMode && (
          <Button
            type="button"
            onClick={() => dispatch(decrementCurrentStep())}
            icon={i18n.language === 'ar' ? <ArrowRight2 size={16} /> : <ArrowLeft2 size={16} />}
            className="rounded-[12px] duration-300 ease-out border text-sm px-6 py-3 flex items-center justify-center gap-2 font-medium bg-white text-gray-900 border-gray-200 hover:bg-gray-50 dark:bg-dark-surface-secondary dark:text-white dark:border-dark-input-border dark:hover:bg-gray-700"
            data-testid="header-back-button"
          >
            {t('components.back')}
          </Button>
        )}

        {!isModalOpen && headerActionButton}
      </div>
    </div>
  );
};

export default ProfileCreationHeader;
